/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable camelcase */
import { FormEvent, useCallback, useContext, useEffect, useState } from 'react';

import { Card } from '@gympass/yoga';
import { ChevronDown, ChevronUp } from '@gympass/yoga-icons';
import { Close } from '@mui/icons-material';
import { isWithinInterval } from 'date-fns';
import isEqual from 'lodash.isequal';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatDate, getFormattedTime } from 'utils/helpers';

import { AgendaContext, NotificationContext } from 'context';
import useDateUtils from 'hooks/useDateUtils';
import useRequest from 'hooks/useRequest';
import {
  AllowDayAndTime,
  getSpecialistAvailability,
  updateRecurrentAvailability,
} from 'services/specialist';

import { LinesLoading } from '..';

import TimeContainer from '../TimeContainer/TimeContainer';

import {
  OccasionalAvailability,
  OccasionalAvailabilityDisplayed,
  Period,
  Time,
} from './types';

import * as S from './styles';

const OccasionalAvailabilityCard: React.FC = () => {
  const intl = useIntl();

  const updateAvailabilityRequest = useRequest();
  const getOccasionalAvailabilityRequest = useRequest();
  const { agenda, setAgenda } = useContext(AgendaContext);
  const { setNotification } = useContext(NotificationContext);

  const MAX_LIST_LENGTH = 10;

  const [availabilities, setAvailabilities] = useState<
    OccasionalAvailability[]
  >([]);
  const [availabilitiesDisplayed, setAvailabilitiesDisplayed] = useState<
    OccasionalAvailabilityDisplayed[]
  >([]);
  const [type, setType] = useState<string>('');
  const [from, setFrom] = useState<Time>({
    date: '',
    hour: '00:00',
  });
  const [until, setUntil] = useState<Time>({
    date: '',
    hour: '00:00',
  });
  const [availableTypes] = useState<Record<string, string>>({
    Available: 'allow_period',
    Block: 'block_period',
  });
  const [availableTypesDisplay] = useState<Record<string, string>>({
    allow_period: 'OCCASIONAL_AVAILABILITY.AVAILABLE',
    block_period: 'OCCASIONAL_AVAILABILITY.BLOCKED',
  });
  const [isListExpanded, setIsListExpanded] = useState<boolean>(false);

  const handleChangeType = useCallback(
    event => setType(event.value),
    [setType],
  );

  const { isDDMMYYYYFormat } = useDateUtils();

  const handleChangeFrom = useCallback(setFrom, [setFrom]);
  const handleChangeUntil = useCallback(setUntil, [setUntil]);

  function isLoading() {
    return (
      getOccasionalAvailabilityRequest.isLoading() ||
      updateAvailabilityRequest.isLoading()
    );
  }

  function filterOccasionalAvailability(constraints: OccasionalAvailability[]) {
    return constraints.filter(a => {
      return a.block_period || a.allow_period;
    });
  }

  async function getOccasionalAvailability() {
    try {
      getOccasionalAvailabilityRequest.setLoading();
      const data = await getSpecialistAvailability();

      setAgenda(data);

      getOccasionalAvailabilityRequest.setSuccess();
    } catch (e) {
      getOccasionalAvailabilityRequest.setError(e);
    }
  }

  function isDuplicated(timeCourse: OccasionalAvailability) {
    const { allow_period: timeCourseAllow, block_period: timeCourseBlock } =
      timeCourse;
    const timeCourseType = timeCourseAllow || timeCourseBlock;

    return (
      availabilities.findIndex(a => {
        const { allow_period, block_period } = a;
        const availabilittyType = allow_period || block_period;

        return isEqual(timeCourseType, availabilittyType);
      }) !== -1
    );
  }

  function timeCourseIntersect(timeCourse: OccasionalAvailability) {
    const { allow_period: timeCourseAllow, block_period: timeCourseBlock } =
      timeCourse;
    const timeCourseType = timeCourseAllow || timeCourseBlock;

    return (
      availabilities.findIndex(a => {
        const { allow_period, block_period } = a;
        const availabilityType = allow_period || block_period;

        if (timeCourseType && availabilityType) {
          const timeCourseStart = new Date(timeCourseType.start);
          const timeCourseEnd = new Date(timeCourseType.end);
          const availabilityTypeStart = new Date(availabilityType.start);
          const availabilityTypeEnd = new Date(availabilityType.end);

          /*
            We should subtract one second in order to not override end dates with start dates
            eg.
            (09:00:00-10:00:00)+(10:00:00-11:00:00) should be possible to book as available
            therefore we should do (09:00:00-09:59:59)+(10:00:00-10:59:59)
          */
          availabilityTypeEnd.setSeconds(availabilityTypeEnd.getSeconds() - 1);
          timeCourseEnd.setSeconds(timeCourseEnd.getSeconds() - 1);

          return (
            isWithinInterval(timeCourseStart, {
              start: availabilityTypeStart,
              end: availabilityTypeEnd,
            }) ||
            isWithinInterval(timeCourseEnd, {
              start: availabilityTypeStart,
              end: availabilityTypeEnd,
            })
          );
        }
        // Case no timecourseType or availabilityType we assume that there is an intercept
        return true;
      }) !== -1
    );
  }

  function formatStringLocaleToDate(dateTimeObj: Time) {
    const { date, hour } = dateTimeObj;

    const [day, month, year] = date.split('/');

    return new Date(`${year}-${month}-${day}T${hour}`);
  }

  async function handleCreateOccasionalAvailability(
    event: FormEvent<HTMLButtonElement>,
  ) {
    event.preventDefault();
    try {
      if (!type) {
        setNotification({
          message: intl.formatMessage({
            id: 'OCCASIONAL_AVAILABILITY.NO_ACTION',
          }),
          type: 'failure',
        });
        return;
      }

      if (!from.date || !until.date) {
        setNotification({
          message: intl.formatMessage({
            id: 'RECURRENT_AVAILABILITY.NEW_AVAILABILITY.NO_SELECTED_DAY',
          }),
          type: 'failure',
        });
        return;
      }

      if (!isDDMMYYYYFormat(from.date) && !isDDMMYYYYFormat(from.until)) {
        setNotification({
          message: intl.formatMessage({
            id: 'OCCASIONAL_AVAILABILITY.INVALID_DATE_FORMAT',
          }),
          type: 'failure',
        });
        return;
      }

      const fromDate = formatStringLocaleToDate(from);
      const toDate = formatStringLocaleToDate(until);

      if (fromDate >= toDate) {
        setNotification({
          message: intl.formatMessage({
            id: 'RECURRENT_AVAILABILITY.NEW_AVAILABILITY.END_AHEAD_START',
          }),
          type: 'failure',
        });
        return;
      }

      const newTimeCourse = {
        [availableTypes[type]]: {
          start: fromDate.toISOString(),
          end: toDate.toISOString(),
        },
      };

      if (isDuplicated(newTimeCourse) || timeCourseIntersect(newTimeCourse)) {
        setNotification({
          message: intl.formatMessage({
            id: 'RECURRENT_AVAILABILITY.NEW_AVAILABILITY.DUPLICITY',
          }),
          type: 'failure',
        });
        return;
      }

      updateAvailabilityRequest.setLoading();

      let newAvailabilityObject: AllowDayAndTime[];
      if (agenda?.availability_constraints) {
        newAvailabilityObject = [
          ...agenda.availability_constraints,
          newTimeCourse,
        ];
      } else {
        newAvailabilityObject = [newTimeCourse];
      }

      await updateRecurrentAvailability(newAvailabilityObject);

      setAgenda({
        ...agenda,
        availability_constraints: newAvailabilityObject,
      });

      updateAvailabilityRequest.setSuccess();
    } catch (e) {
      setNotification({
        message: intl.formatMessage({
          id: 'ERROR.TRY',
        }),
        type: 'failure',
      });

      updateAvailabilityRequest.setError(e);
    }
  }

  async function deleteOccasionalAvailability(
    availabilityDisplayed: OccasionalAvailabilityDisplayed,
  ) {
    updateAvailabilityRequest.setLoading();
    try {
      const availabilityType = Object.keys(availableTypesDisplay).find(
        key => availableTypesDisplay[key] === availabilityDisplayed.type,
      );

      if (availabilityType) {
        const fromDate = formatStringLocaleToDate({
          date: availabilityDisplayed.startDate,
          hour: availabilityDisplayed.startHour,
        } as Time);
        const toDate = formatStringLocaleToDate({
          date: availabilityDisplayed.endDate,
          hour: availabilityDisplayed.endHour,
        } as Time);

        const availability: OccasionalAvailability = {
          [availabilityType]: {
            start: fromDate.toISOString(),
            end: toDate.toISOString(),
          },
        };

        if (agenda?.availability_constraints) {
          const newAvailabilities = [...agenda.availability_constraints];

          const availabilityToDeleteIndex =
            agenda.availability_constraints.findIndex(a => {
              return isEqual(a, availability);
            });

          newAvailabilities.splice(availabilityToDeleteIndex, 1);

          await updateRecurrentAvailability(newAvailabilities);

          setAgenda({
            ...agenda,
            availability_constraints: newAvailabilities,
          });
        }
        updateAvailabilityRequest.setSuccess();
      } else {
        setNotification({
          message: intl.formatMessage({
            id: 'ERROR.TRY',
          }),
          type: 'failure',
        });
        updateAvailabilityRequest.setError('Not availabilityType found');
      }
    } catch (e) {
      setNotification({
        message: intl.formatMessage({
          id: 'ERROR.TRY',
        }),
        type: 'failure',
      });

      if (typeof e === 'string') {
        updateAvailabilityRequest.setError(e);
      }
    }
  }

  function handleAvailabilitySort(availabilityList: OccasionalAvailability[]) {
    return availabilityList.sort((a, b) => {
      const { allow_period: allowA, block_period: blockA } = a;
      const { allow_period: allowB, block_period: blockB } = b;

      const availabilityA = allowA || blockA;
      const availabilityB = allowB || blockB;

      if (availabilityA && availabilityB) {
        const aDate = new Date(availabilityA?.start);
        const bDate = new Date(availabilityB?.start);

        if (aDate > bDate) {
          return 1;
        }

        if (aDate < bDate) {
          return -1;
        }

        return 0;
      }

      return 0;
    });
  }

  function handleFormatAvailabilitiesToDisplay(
    availabilitiesList: OccasionalAvailability[],
  ) {
    return availabilitiesList
      .filter(availability => {
        const { allow_period, block_period } = availability;
        return allow_period || block_period;
      })
      .map(availability => {
        const { allow_period, block_period } = availability;

        const availabilityDisplay = (allow_period || block_period) as Period;

        const availabilityType =
          availableTypesDisplay[Object.keys(availability)[0]];

        const availabilityStart = new Date(availabilityDisplay.start);
        const availabilityEnd = new Date(availabilityDisplay.end);

        const availabilityStartDate = formatDate(availabilityStart, {
          locale: intl.locale,
        });
        const availabilityStartHour = getFormattedTime(availabilityStart, {
          locale: intl.locale,
        });

        const availabilityEndDate = formatDate(availabilityEnd, {
          locale: intl.locale,
        });
        const availabilityEndHour = getFormattedTime(availabilityEnd, {
          locale: intl.locale,
        });

        return {
          type: availabilityType,
          startDate: availabilityStartDate,
          startHour: availabilityStartHour,
          endDate: availabilityEndDate,
          endHour: availabilityEndHour,
        };
      });
  }

  function handleAvailabilityListVisualisation() {
    let availabilitiesToDisplay: OccasionalAvailability[] = [];

    availabilitiesToDisplay = handleAvailabilitySort(availabilities);

    if (isListExpanded) {
      availabilitiesToDisplay = availabilitiesToDisplay.slice(
        0,
        MAX_LIST_LENGTH,
      );
    }

    const formattedAvailabilities = handleFormatAvailabilitiesToDisplay(
      availabilitiesToDisplay,
    );

    setAvailabilitiesDisplayed(formattedAvailabilities);
    setIsListExpanded(!isListExpanded);
  }

  useEffect(() => {
    if (agenda === null) {
      getOccasionalAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAvailabilities(
      filterOccasionalAvailability(
        agenda?.availability_constraints?.length
          ? agenda.availability_constraints
          : [],
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agenda]);

  useEffect(() => {
    let availabilitiesToDisplay: OccasionalAvailability[];

    availabilitiesToDisplay = handleAvailabilitySort(availabilities);

    if (!isListExpanded) {
      availabilitiesToDisplay = availabilitiesToDisplay.slice(
        0,
        MAX_LIST_LENGTH,
      );
    }

    const formattedAvailabilities = handleFormatAvailabilitiesToDisplay(
      availabilitiesToDisplay,
    );

    setAvailabilitiesDisplayed(formattedAvailabilities);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilities]);

  return (
    <Card>
      <S.CardTitle data-testid="cardTitle">
        <FormattedMessage id="CARE_PROVIDER.OCCASIONAL.TITLE" />
      </S.CardTitle>
      <S.CardSubTitle data-testid="cardSubTitle">
        <FormattedMessage id="CARE_PROVIDER.OCCASIONAL.SUBTITLE" />
      </S.CardSubTitle>

      <S.ContentContainer>
        <S.AvailabilitySections data-testid="availabilityForm">
          <S.FormHeading>
            <FormattedMessage id="CARE_PROVIDER.OCCASIONAL.FORM_HEADING" />
          </S.FormHeading>
          <S.Form>
            <S.TypeDropdown
              label={intl.formatMessage({
                id: 'CARE_PROVIDER.OCCASIONAL.TYPE_DROPDOWN',
              })}
              full
              onChange={handleChangeType}
              options={[
                {
                  label: intl.formatMessage({
                    id: 'CARE_PROVIDER.OCCASIONAL.TYPE_DROPDOWN.AVAILABLE',
                  }),
                  value: 'Available',
                },
                {
                  label: intl.formatMessage({
                    id: 'CARE_PROVIDER.OCCASIONAL.TYPE_DROPDOWN.BLOCK',
                  }),
                  value: 'Block',
                },
              ]}
              data-testid="typeDropdown"
            />

            <S.TimeHeading>
              <FormattedMessage id="OCCASIONAL_AVAILABILITY.FROM" />
            </S.TimeHeading>
            <TimeContainer data-testid="from" onChangeTime={handleChangeFrom} />

            <S.TimeHeading>
              <FormattedMessage id="OCCASIONAL_AVAILABILITY.UNTIL" />
            </S.TimeHeading>
            <TimeContainer data-testid="to" onChangeTime={handleChangeUntil} />

            <S.AddButton
              data-testid="addButton"
              onClick={handleCreateOccasionalAvailability}
              full
            >
              <FormattedMessage id="CARE_PROVIDER.OCCASIONAL.ADD_BUTTON" />
            </S.AddButton>
          </S.Form>
        </S.AvailabilitySections>

        <S.AvailabilitySections data-testid="availabilityList">
          <S.FormHeading>
            <FormattedMessage id="CARE_PROVIDER.OCCASIONAL.LIST_HEADING" />
          </S.FormHeading>
          {!!availabilitiesDisplayed?.length &&
            !isLoading() &&
            availabilitiesDisplayed.map((availability, index) => (
              <S.StyledBox key={index.toString()} data-testid="availability">
                <S.ListItemText variant="body2">
                  {`${intl.formatMessage({ id: availability.type })} `}
                  <FormattedMessage id="CARE_PROVIDER.OCCASIONAL.LIST.FROM" />
                  {` ${availability.startDate}, `}
                  <FormattedMessage id="CARE_PROVIDER.OCCASIONAL.LIST.AT" />
                  {`${availability.startHour}, `}
                  <FormattedMessage id="CARE_PROVIDER.OCCASIONAL.LIST.UNTIL" />
                  {` ${availability.endDate}, `}
                  <FormattedMessage id="CARE_PROVIDER.OCCASIONAL.LIST.AT" />
                  {`${availability.endHour}`}
                </S.ListItemText>
                <S.DeleteAvailabilityButton
                  data-testid="deleteAvailability"
                  onClick={() => deleteOccasionalAvailability(availability)}
                >
                  <Close />
                </S.DeleteAvailabilityButton>
              </S.StyledBox>
            ))}
          {isLoading() && (
            <LinesLoading
              lines={
                availabilitiesDisplayed.length > MAX_LIST_LENGTH
                  ? availabilitiesDisplayed.length
                  : MAX_LIST_LENGTH
              }
            />
          )}
          {!isLoading() && !(availabilitiesDisplayed.length > 0) && (
            <>
              <S.AvailabilityEmptyState data-testid="emptyState">
                <FormattedMessage id="OCCASIONAL_AVAILABILITY.EMPTY_STATE" />
              </S.AvailabilityEmptyState>
            </>
          )}
          {availabilities.length > MAX_LIST_LENGTH && (
            <>
              <S.StyledDivisor />
              <S.ListAccordionButton
                icon={isListExpanded ? ChevronUp : ChevronDown}
                onClick={handleAvailabilityListVisualisation}
                data-testid="expandButton"
              >
                {isListExpanded ? (
                  <FormattedMessage id="RECURRENT_AVAILABILITY.AVAILABILITY.SEE_LESS" />
                ) : (
                  <FormattedMessage id="RECURRENT_AVAILABILITY.AVAILABILITY.SEE_MORE" />
                )}
              </S.ListAccordionButton>
            </>
          )}
        </S.AvailabilitySections>
      </S.ContentContainer>
    </Card>
  );
};

export default OccasionalAvailabilityCard;
